// "Record (they said yes)" (owner request, PJ, 2026-10-20: "I want the meetings that we booked to have been recorded or
// something after and then be posted somewhere in the Discord or the dialer for us to listen to the meetings that were
// booked."). Pure: what the call bar shows for recording with the prospect's yes, so the rules are unit-tested.
//
// Only on a call whose prepare answer carried consentRecording (the admin setting consent_recording_enabled is on and the
// recording rule skips the call for all-party consent: a US all-party state or a California / Washington area code;
// never Canada), only while that call is connected (in_call). The button opens a confirm (ConsentRecordConfirm) that
// tells the setter to ask first; only "They said yes, start" calls the server. Once on, the bar shows that it is on and a
// "Stop recording" button for when they take the yes back. Every failure is one plain line under the call bar; the call
// goes on either way.
import type { CallMachine } from './machine';
export { CONSENT_RECORDING_ASK, CONSENT_RECORDING_CONFIRM, CONSENT_RECORDING_ON } from '../../../src/shared/api-telephony';

/** unavailable: the server refused for good on this call (setting off, not a consent call...): no button, the reason stays. */
export type ConsentRecPhase = 'offer' | 'confirm' | 'starting' | 'on' | 'stopping' | 'stopped' | 'unavailable';

/** The offer for one call (DialerState.consentRec): which call, where it stands, and the last refusal in one sentence. */
export interface ConsentRecUi {
  callId: number;
  phase: ConsentRecPhase;
  error: string | null;
}

export type ConsentRecView =
  | { kind: 'hidden' }
  | { kind: 'offer'; busy: boolean }             // "Record (they said yes)"; busy while the confirm is open or it starts
  | { kind: 'on'; busy: boolean }                // recording: a chip and "Stop recording" (busy while it stops)
  | { kind: 'stopped' };                         // they took it back: a chip, nothing more on this call

/** The button or chip for the call on screen: nothing unless the offer belongs to this call and it is connected. */
export function consentRecView(call: Pick<CallMachine, 'phase' | 'callId'>, ui: ConsentRecUi | null): ConsentRecView {
  if (!ui || call.callId === null || ui.callId !== call.callId || call.phase !== 'in_call') return { kind: 'hidden' };
  switch (ui.phase) {
    case 'offer': return { kind: 'offer', busy: false };
    case 'confirm':
    case 'starting': return { kind: 'offer', busy: true };
    case 'on': return { kind: 'on', busy: false };
    case 'stopping': return { kind: 'on', busy: true };
    case 'stopped': return { kind: 'stopped' };
    case 'unavailable': return { kind: 'hidden' };
  }
}

/** Whether the confirm dialog is on screen: asked for, for the connected call it belongs to. */
export function consentConfirmOpen(call: Pick<CallMachine, 'phase' | 'callId'>, ui: ConsentRecUi | null): boolean {
  return !!ui && ui.phase === 'confirm' && call.phase === 'in_call' && ui.callId === call.callId;
}

/** The phase after a refused start: already started (another tab) shows it on; a refusal that can't change on this call
 *  takes the button away ('unavailable': setting off, not a consent call, recorded anyway, not connected); anything else (a
 *  Twilio hiccup) leaves it to press again. */
export function phaseAfterRefusal(code: string | null): ConsentRecPhase {
  if (code === 'consent_already_started') return 'on';
  if (code === 'consent_recording_off' || code === 'not_consent_call' || code === 'not_outbound' || code === 'call_not_live' || code === 'already_recording') return 'unavailable';
  return 'offer';
}

/** The confirm's keys (review 2026-10-20): Esc cancels; H hangs up and M mutes, as they do on the call bar, because the
 *  dialer's own hotkeys stand down while any dialog is open (pages/Dialer.tsx) and the backdrop covers the call bar's
 *  buttons. H or M held down (repeat) is swallowed. Modifier combinations and every other key are not the confirm's;
 *  nothing starts the recording from the keyboard (a click on purpose, or Enter on that focused button). */
export function consentConfirmKey(e: { key: string; repeat?: boolean; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean }):
  'cancel' | 'hangup' | 'mute' | 'swallow' | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === 'Escape') return 'cancel';
  const k = e.key.length === 1 ? e.key.toUpperCase() : '';
  if (k !== 'H' && k !== 'M') return null;
  if (e.repeat) return 'swallow';
  return k === 'H' ? 'hangup' : 'mute';
}
